import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Alert, Card, Form, Spinner } from 'react-bootstrap';
import authService from '../services/authService';
import BackToDashboard from '../components/BackToDashboard';

function EmailWelcome() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState('generate'); // generate, generating, editing
  const [error, setError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);

  // Content data
  const [emailSequence, setEmailSequence] = useState('');
  const [savedContent, setSavedContent] = useState(null);
  const [leadMagnetTopic, setLeadMagnetTopic] = useState('');
  const [targetMarket, setTargetMarket] = useState('');

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkAuth = async () => {
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Get target market for personalized title
    try {
      const marketResponse = await fetch('/api/target-market/primary', {
        credentials: 'include'
      });

      if (marketResponse.ok) {
        const marketData = await marketResponse.json();
        if (marketData.primary_niche) {
          setTargetMarket(marketData.primary_niche.niche_name || '');
        }
      }
    } catch (err) {
      console.error('Error loading target market:', err);
    }

    // Get lead magnet topic
    try {
      const topicResponse = await fetch('/api/assets/lead-magnet/get-topic', {
        credentials: 'include'
      });

      if (topicResponse.ok) {
        const topicData = await topicResponse.json();
        if (topicData.topic && topicData.topic.topic) {
          setLeadMagnetTopic(topicData.topic.topic);
        }
      }
    } catch (err) {
      console.error('Error loading lead magnet topic:', err);
    }

    // Check if user has saved welcome email sequence
    try {
      const contentResponse = await fetch('/api/assets/email-welcome/get', {
        credentials: 'include'
      });

      if (contentResponse.ok) {
        const contentData = await contentResponse.json();
        if (contentData.sequence) {
          setSavedContent(contentData.sequence);
          setEmailSequence(contentData.sequence);
          setStage('editing');
        }
      }
    } catch (err) {
      console.error('Error loading saved data:', err);
    }

    setLoading(false);
  };

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    setStage('generating');

    try {
      const response = await fetch('/api/assets/email-welcome/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setEmailSequence(data.sequence);
        setStage('editing');
      } else {
        const errorData = await response.json();
        setError(`Failed to generate content: ${errorData.error || 'Unknown error'}`);
        setStage('generate');
      }
    } catch (err) {
      setError('Error generating content: ' + err.message);
      setStage('generate');
    }

    setGenerating(false);
  };

  const handleSave = async () => {
    if (!emailSequence || !emailSequence.trim()) {
      setError('Please enter your welcome email sequence content');
      return;
    }

    setSaving(true);
    setError('');

    try {
      const response = await fetch('/api/assets/email-welcome/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ sequence: emailSequence })
      });

      if (response.ok) {
        setSavedContent(emailSequence);
        // Show success message
        setError('');
        alert('Welcome email sequence saved successfully!');
      } else {
        const errorData = await response.json();
        setError(`Failed to save welcome email sequence: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      setError('Error saving welcome email sequence: ' + err.message);
    }

    setSaving(false);
  };

  const handleContinue = () => {
    navigate('/email-sequences');
  };

  if (loading) {
    return (
      <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <BackToDashboard module="assets-creation" />
      <Row className="mb-4">
        <Col>
          <h1>Welcome Email Sequence{targetMarket && ` for ${targetMarket}`}</h1>
          <p className="text-muted">
            Create a 6-email welcome sequence to nurture new lead magnet subscribers and build trust.
          </p>
          {leadMagnetTopic && (
            <Alert variant="info">
              <strong>Lead Magnet:</strong> {leadMagnetTopic}
            </Alert>
          )}
        </Col>
      </Row>

      {error && !error.includes('saved successfully') && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      {/* Generate Stage */}
      {stage === 'generate' && (
        <Row>
          <Col md={8}>
            <Card>
              <Card.Body>
                <h4 className="mb-4">📧 Generate Welcome Email Sequence</h4>
                <p className="mb-4">
                  Our AI will create a complete 6-email welcome sequence to nurture new subscribers and build trust.
                </p>

                <Alert variant="info" className="mb-4">
                  <strong>What you'll get - 6 complete emails:</strong>
                  <ul className="mb-0 mt-2">
                    <li><strong>Email 1:</strong> Welcome & deliver lead magnet</li>
                    <li><strong>Email 2:</strong> Quick win with lead magnet content</li>
                    <li><strong>Email 3:</strong> Your story & emotional connection</li>
                    <li><strong>Email 4:</strong> Value-packed insight & expertise</li>
                    <li><strong>Email 5:</strong> Bridge to core product/next step</li>
                    <li><strong>Email 6:</strong> Clear call-to-action</li>
                  </ul>
                </Alert>

                <div className="d-grid">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={handleGenerate}
                    disabled={generating}
                  >
                    {generating ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generating Email Sequence... (45-60 seconds)
                      </>
                    ) : (
                      'Generate Welcome Sequence →'
                    )}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col md={4}>
            <Card className="bg-light">
              <Card.Body>
                <h5 className="mb-3">💡 Email Tips</h5>
                <ul className="small mb-0">
                  <li>Add personal stories & examples</li>
                  <li>Customize subject lines for your voice</li>
                  <li>Include real testimonials where noted</li>
                  <li>Adjust timing between emails if needed</li>
                  <li>Test on mobile before sending</li>
                  <li>Use your brand voice throughout</li>
                </ul>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Generating Stage */}
      {stage === 'generating' && (
        <Row>
          <Col md={12}>
            <Card>
              <Card.Body className="text-center py-5">
                <Spinner animation="border" className="mb-3" />
                <h4>Generating Your Welcome Email Sequence...</h4>
                <p className="text-muted">
                  This will take about 45-60 seconds. We're creating 6 complete emails to nurture your new subscribers and build trust.
                </p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Editing Stage */}
      {stage === 'editing' && (
        <>
          <Row className="mb-4">
            <Col md={12}>
              <Card>
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h4 className="mb-0">✏️ Review & Edit Welcome Email Sequence</h4>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleGenerate}
                      disabled={generating}
                    >
                      {generating ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Regenerating...
                        </>
                      ) : (
                        'Regenerate Sequence'
                      )}
                    </Button>
                  </div>

                  <Alert variant="info" className="mb-4">
                    <strong>Review your AI-generated email sequence below.</strong> This includes 6 complete emails with subject lines, previews, and full body copy. Edit directly or regenerate.
                  </Alert>

                  <Form.Group className="mb-4">
                    <Form.Label><strong>Welcome Email Sequence (6 Emails):</strong></Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={30}
                      value={emailSequence || ''}
                      onChange={(e) => setEmailSequence(e.target.value)}
                      style={{ fontFamily: 'monospace', fontSize: '14px' }}
                    />
                    <Form.Text className="text-muted">
                      Edit subject lines, personalize stories, add real testimonials, and adjust timing as needed.
                    </Form.Text>
                  </Form.Group>

                  <div className="d-grid gap-2">
                    <Button
                      variant="primary"
                      size="lg"
                      onClick={handleSave}
                      disabled={saving || !emailSequence || !emailSequence.trim()}
                    >
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : (
                        'Save Email Sequence'
                      )}
                    </Button>

                    {savedContent && (
                      <Button
                        variant="outline-primary"
                        size="lg"
                        onClick={handleContinue}
                      >
                        Return to Email Sequences →
                      </Button>
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {savedContent && (
            <Row>
              <Col md={12}>
                <Alert variant="success">
                  <strong>✓ Welcome Sequence Saved</strong><br />
                  Your 6-email welcome sequence is saved. You can make additional edits here or return to create other email sequences.
                </Alert>
              </Col>
            </Row>
          )}
        </>
      )}
    </Container>
  );
}

export default EmailWelcome;
